/**
 * Plan and subscription context. Exposes current plan and feature access.
 * Admins and super admins get all features regardless of plan.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { isAdminRole } from '../constants/roles';
import {
  PLAN_IDS,
  PLANS,
  getPlanById,
  planIncludes,
  getRequiredPlanForFeature,
  getFeatureKeyForPath,
  FEATURES,
} from '../constants/plans';

const PlanContext = createContext();

export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return ctx;
}

export function PlanProvider({ children }) {
  const { userProfile, loading: authLoading } = useAuth();

  const value = useMemo(() => {
    const planId = userProfile?.planId || PLAN_IDS.BASIC;
    const plan = getPlanById(planId);
    const isAdmin = userProfile && isAdminRole(userProfile);

    /** Admins get all features; otherwise check plan tier. */
    function hasFeature(featureKey) {
      if (!featureKey) return true;
      if (isAdmin) return true;
      const required = getRequiredPlanForFeature(featureKey);
      return planIncludes(planId, required);
    }

    /** True if this feature is gated by a plan higher than Basic. */
    function isPremiumFeature(featureKey) {
      const required = getRequiredPlanForFeature(featureKey);
      return required !== PLAN_IDS.BASIC;
    }

    /** Get feature key for current route path (for overlay messaging). */
    function getFeatureForPath(pathname) {
      return getFeatureKeyForPath(pathname);
    }

    return {
      planId,
      plan,
      plans: PLANS,
      hasFeature,
      isPremiumFeature,
      getFeatureForPath,
      showAds: plan.showAds === true,
      subscriptionStatus: userProfile?.subscriptionStatus ?? null,
      subscriptionEndsAt: userProfile?.subscriptionEndsAt ?? null,
      loading: authLoading,
    };
  }, [userProfile, authLoading]);

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
}

export { FEATURES };
